import { PageTitle, useLayout } from '../../../_metronic/layout/core';
import { DefaultTitle } from '../../../_metronic/layout/components/header/page-title/DefaultTitle';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { IRazaoLayoutConfig } from '../../interfaces/IRazaoLayoutConfig';
import razaoLayoutConfigService from '../../services/razaoLayoutConfigService';

// A=1, B=2 ... Z=26
const COL_OPTIONS = Array.from({ length: 26 }, (_, i) => ({
  value: i + 1,
  label: `${String.fromCharCode(65 + i)} (${i + 1})`,
}));

type ColSelectProps = {
  label: string;
  value: number | undefined;
  onChange: (v: number) => void;
  required?: boolean;
  hint?: string;
};

function ColSelect({ label, value, onChange, required, hint }: ColSelectProps) {
  return (
    <div className='mb-4'>
      <label className='form-label fw-semibold fs-7'>
        {label} {required && <span className='text-danger'>*</span>}
      </label>
      <select
        className='form-select form-select-sm form-select-solid'
        value={value ?? ''}
        onChange={e => onChange(parseInt(e.target.value))}
        required={required}
      >
        <option value=''>— selecione —</option>
        {COL_OPTIONS.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {hint && <div className='form-text text-muted'>{hint}</div>}
    </div>
  );
}

type TextFieldProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
  hint?: string;
};

function TextField({ label, value, onChange, required, placeholder, hint }: TextFieldProps) {
  return (
    <div className='mb-4'>
      <label className='form-label fw-semibold fs-7'>
        {label} {required && <span className='text-danger'>*</span>}
      </label>
      <input
        type='text'
        className='form-control form-control-sm form-control-solid'
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
      />
      {hint && <div className='form-text text-muted'>{hint}</div>}
    </div>
  );
}

const emptyForm = (): Omit<IRazaoLayoutConfig, 'id' | 'dataCadastro' | 'dataAtualizacao'> => ({
  tipoRazao: '',
  descricao: '',

  empresaIdTexto: 'Empresa:',
  empresaIdColuna: 1,
  empresaValorColuna: 3,

  cnpjIdTexto: 'C.N.P.J.:',
  cnpjIdColuna: 1,
  cnpjValorColuna: 3,

  periodoIdTexto: 'Período:',
  periodoIdColuna: 1,
  periodoValorColuna: 3,

  contaIdTexto: 'Conta:',
  contaIdColuna: 1,
  contaCodigoColuna: 3,
  contaNomeColuna: 5,

  saldoAnteriorIdTexto: 'SALDO ANTERIOR',
  saldoAnteriorIdColuna: 3,
  saldoAnteriorValorColuna: 13,

  dataColuna: 1,
  loteColuna: 2,
  historicoColuna: 3,
  contaPartColuna: 7,
  debitoColuna: 8,
  creditoColuna: 9,
  saldoColuna: 10,
  saldoExercicioColuna: 13,

  linhasCabecalhoPular: 0,
  ativo: true,
});

function FormRazaoLayout() {
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const { classes } = useLayout();
  const isEdit = !!id;

  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      setLoading(true);
      const data = await razaoLayoutConfigService.getById(parseInt(id!));
      if (data) {
        const { id: _id, dataCadastro: _dc, dataAtualizacao: _da, ...rest } = data;
        setForm(rest as any);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    let ok = false;
    if (isEdit) {
      ok = await razaoLayoutConfigService.update(parseInt(id!), { id: parseInt(id!), ...form });
    } else {
      const created = await razaoLayoutConfigService.create(form);
      ok = !!created;
    }

    setSaving(false);
    if (ok) {
      await Swal.fire({ text: isEdit ? 'Layout atualizado!' : 'Layout criado!', timer: 2000, icon: 'success', showConfirmButton: false });
      history.push('/parametrizacoes/razao/layouts');
    }
  };

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center py-20'>
        <div className='spinner-border text-primary' role='status' />
      </div>
    );
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>{isEdit ? 'Editar Layout Razão' : 'Novo Layout Razão'}</PageTitle>
      <div className='toolbar' id='kt_toolbar'>
        <div id='kt_toolbar_container' className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}>
          <DefaultTitle />
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* ── IDENTIFICAÇÃO ── */}
        <div className='card mb-6'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title fw-bold fs-5'>Identificação</h3>
          </div>
          <div className='card-body row g-4'>
            <div className='col-md-4'>
              <TextField
                label='Tipo (chave única)'
                value={form.tipoRazao}
                onChange={v => set('tipoRazao', v)}
                required
                placeholder='ex: razao-dominio'
                hint='Use kebab-case. Ex: razao-dominio, razao-sap'
              />
            </div>
            <div className='col-md-6'>
              <TextField
                label='Descrição'
                value={form.descricao}
                onChange={v => set('descricao', v)}
                required
                placeholder='ex: Razão Contábil - Sistema Domínio'
              />
            </div>
            <div className='col-md-2'>
              <div className='mb-4'>
                <label className='form-label fw-semibold fs-7'>Ativo</label>
                <div className='form-check form-switch mt-2'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={form.ativo}
                    onChange={e => set('ativo', e.target.checked)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── EMPRESA ── */}
        <div className='card mb-6'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title fw-bold fs-5'>Identificação da Empresa no Arquivo</h3>
            <span className='text-muted fs-7 ms-3'>Linha onde está o nome da empresa</span>
          </div>
          <div className='card-body row g-4'>
            <div className='col-md-4'>
              <TextField
                label='Texto identificador'
                value={form.empresaIdTexto ?? ''}
                onChange={v => set('empresaIdTexto', v)}
                placeholder='ex: Empresa:'
                hint='Conteúdo exato da célula que identifica a linha'
              />
            </div>
            <div className='col-md-4'>
              <ColSelect
                label='Coluna do identificador'
                value={form.empresaIdColuna}
                onChange={v => set('empresaIdColuna', v)}
                required
                hint='Coluna onde está o texto identificador'
              />
            </div>
            <div className='col-md-4'>
              <ColSelect
                label='Coluna do valor (nome da empresa)'
                value={form.empresaValorColuna}
                onChange={v => set('empresaValorColuna', v)}
                required
                hint='Coluna onde está o nome da empresa'
              />
            </div>
          </div>
        </div>

        {/* ── CNPJ ── */}
        <div className='card mb-6'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title fw-bold fs-5'>CNPJ</h3>
          </div>
          <div className='card-body row g-4'>
            <div className='col-md-4'>
              <TextField
                label='Texto identificador'
                value={form.cnpjIdTexto ?? ''}
                onChange={v => set('cnpjIdTexto', v)}
                placeholder='ex: C.N.P.J.:'
              />
            </div>
            <div className='col-md-4'>
              <ColSelect
                label='Coluna do identificador'
                value={form.cnpjIdColuna}
                onChange={v => set('cnpjIdColuna', v)}
                required
              />
            </div>
            <div className='col-md-4'>
              <ColSelect
                label='Coluna do valor (CNPJ)'
                value={form.cnpjValorColuna}
                onChange={v => set('cnpjValorColuna', v)}
                required
              />
            </div>
          </div>
        </div>

        {/* ── PERÍODO ── */}
        <div className='card mb-6'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title fw-bold fs-5'>Período</h3>
          </div>
          <div className='card-body row g-4'>
            <div className='col-md-4'>
              <TextField
                label='Texto identificador'
                value={form.periodoIdTexto ?? ''}
                onChange={v => set('periodoIdTexto', v)}
                placeholder='ex: Período:'
              />
            </div>
            <div className='col-md-4'>
              <ColSelect
                label='Coluna do identificador'
                value={form.periodoIdColuna}
                onChange={v => set('periodoIdColuna', v)}
                required
              />
            </div>
            <div className='col-md-4'>
              <ColSelect
                label='Coluna do valor (período)'
                value={form.periodoValorColuna}
                onChange={v => set('periodoValorColuna', v)}
                required
              />
            </div>
          </div>
        </div>

        {/* ── CONTA ── */}
        <div className='card mb-6'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title fw-bold fs-5'>Cabeçalho de Conta</h3>
            <span className='text-muted fs-7 ms-3'>Linha que indica início de uma nova conta</span>
          </div>
          <div className='card-body row g-4'>
            <div className='col-md-3'>
              <TextField
                label='Texto identificador'
                value={form.contaIdTexto}
                onChange={v => set('contaIdTexto', v)}
                required
                placeholder='ex: Conta:'
              />
            </div>
            <div className='col-md-3'>
              <ColSelect
                label='Coluna do identificador'
                value={form.contaIdColuna}
                onChange={v => set('contaIdColuna', v)}
                required
              />
            </div>
            <div className='col-md-3'>
              <ColSelect
                label='Coluna do código da conta'
                value={form.contaCodigoColuna}
                onChange={v => set('contaCodigoColuna', v)}
                required
              />
            </div>
            <div className='col-md-3'>
              <ColSelect
                label='Coluna do nome da conta'
                value={form.contaNomeColuna}
                onChange={v => set('contaNomeColuna', v)}
                required
              />
            </div>
          </div>
        </div>

        {/* ── SALDO ANTERIOR ── */}
        <div className='card mb-6'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title fw-bold fs-5'>Saldo Anterior (inicial)</h3>
          </div>
          <div className='card-body row g-4'>
            <div className='col-md-4'>
              <TextField
                label='Texto identificador'
                value={form.saldoAnteriorIdTexto}
                onChange={v => set('saldoAnteriorIdTexto', v)}
                required
                placeholder='ex: SALDO ANTERIOR'
              />
            </div>
            <div className='col-md-4'>
              <ColSelect
                label='Coluna do identificador'
                value={form.saldoAnteriorIdColuna}
                onChange={v => set('saldoAnteriorIdColuna', v)}
                required
              />
            </div>
            <div className='col-md-4'>
              <ColSelect
                label='Coluna do valor do saldo'
                value={form.saldoAnteriorValorColuna}
                onChange={v => set('saldoAnteriorValorColuna', v)}
                required
              />
            </div>
          </div>
        </div>

        {/* ── COLUNAS DE LANÇAMENTO ── */}
        <div className='card mb-6'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title fw-bold fs-5'>Colunas dos Lançamentos</h3>
            <span className='text-muted fs-7 ms-3'>Posição de cada campo nas linhas de transação</span>
          </div>
          <div className='card-body row g-4'>
            <div className='col-md-3'>
              <ColSelect label='Data' value={form.dataColuna} onChange={v => set('dataColuna', v)} required />
            </div>
            <div className='col-md-3'>
              <ColSelect label='Lote / Documento (opcional)' value={form.loteColuna} onChange={v => set('loteColuna', v)} />
            </div>
            <div className='col-md-3'>
              <ColSelect label='Histórico / Descrição' value={form.historicoColuna} onChange={v => set('historicoColuna', v)} required />
            </div>
            <div className='col-md-3'>
              <ColSelect label='Conta Contrapartida (opcional)' value={form.contaPartColuna} onChange={v => set('contaPartColuna', v)} />
            </div>
            <div className='col-md-3'>
              <ColSelect label='Débito' value={form.debitoColuna} onChange={v => set('debitoColuna', v)} required />
            </div>
            <div className='col-md-3'>
              <ColSelect label='Crédito' value={form.creditoColuna} onChange={v => set('creditoColuna', v)} required />
            </div>
            <div className='col-md-3'>
              <ColSelect label='Saldo corrente' value={form.saldoColuna} onChange={v => set('saldoColuna', v)} required />
            </div>
            <div className='col-md-3'>
              <ColSelect label='Saldo exercício (opcional)' value={form.saldoExercicioColuna} onChange={v => set('saldoExercicioColuna', v)} />
            </div>
          </div>
        </div>

        {/* ── MODO PARTICIPANTE ── */}
        <div className='card mb-6'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title fw-bold fs-5'>Modo Participante (opcional)</h3>
            <span className='text-muted fs-7 ms-3'>
              Quando preenchido, ativa o modo flat: cada linha contém o CNPJ/CPF do participante
              e o código do cartão é derivado automaticamente (dígitos antes do '/', sem pontos).
              Substitui o mecanismo de blocos de conta com cabeçalho.
            </span>
          </div>
          <div className='card-body row g-4'>
            <div className='col-md-4'>
              <ColSelect
                label='Coluna CNPJ/CPF do participante'
                value={form.cnpjParticipanteColuna}
                onChange={v => set('cnpjParticipanteColuna', v)}
                hint='Ativa o modo participante. Ex: coluna V para "CNPJ/CPF"'
              />
            </div>
            <div className='col-md-4'>
              <ColSelect
                label='Coluna nome do participante'
                value={form.nomeParticipanteColuna}
                onChange={v => set('nomeParticipanteColuna', v)}
                hint='Usado como descrição do cartão. Ex: coluna X para "Descrição Participante"'
              />
            </div>
            <div className='col-md-4'>
              <ColSelect
                label='Coluna conta original (referencial ECD)'
                value={form.contaRefOriginalColuna}
                onChange={v => set('contaRefOriginalColuna', v)}
                hint='Conta do arquivo usada para lookup no ECD. Ex: coluna B para "Cta Red"'
              />
            </div>
          </div>
        </div>

        {/* ── CONFIGURAÇÕES EXTRAS ── */}
        <div className='card mb-6'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title fw-bold fs-5'>Configurações Extras</h3>
          </div>
          <div className='card-body row g-4'>
            <div className='col-md-3'>
              <div className='mb-4'>
                <label className='form-label fw-semibold fs-7'>Linhas de cabeçalho a pular</label>
                <input
                  type='number'
                  min={0}
                  className='form-control form-control-sm form-control-solid'
                  value={form.linhasCabecalhoPular}
                  onChange={e => set('linhasCabecalhoPular', parseInt(e.target.value) || 0)}
                />
                <div className='form-text text-muted'>Linhas iniciais do arquivo que devem ser ignoradas</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── AÇÕES ── */}
        <div className='d-flex justify-content-end gap-3 mb-8'>
          <button
            type='button'
            className='btn btn-light'
            onClick={() => history.push('/parametrizacoes/razao/layouts')}
          >
            Cancelar
          </button>
          <button type='submit' className='btn btn-primary' disabled={saving}>
            {saving ? (
              <span className='indicator-label'>
                <span className='spinner-border spinner-border-sm me-2' />
                Salvando...
              </span>
            ) : (
              isEdit ? 'Salvar Alterações' : 'Criar Layout'
            )}
          </button>
        </div>
      </form>
    </>
  );
}

export default FormRazaoLayout;
